import { memo, useCallback, useContext, useEffect, useRef } from "react";

import { useTranslator } from "@eo-locale/react";

import { isEmpty } from "lodash";

import Cookies from "js-cookie";

import { buildImgixUrl, dateWithSiteLocale, stripAllHtml } from "@/lib";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  Contribution,
  FiArrowUp,
  HStack,
  Heading,
  IconButton,
  Image,
  Text,
  useMediaQuery
} from "@/storybook-components";
import MarkerDetails from "./marker-details";
import ActionList from "@/components/places/esri-map/action-items";
import PinIcon from "@/components/places/esri-map/markers/pin-Icon";
import SortPins from "@/components/places/esri-map/sort-pins";
import { addAssetPrefix } from "@/helpers/add-asset-prefix";

import { EsriContext } from "@/components/places/context/esri-context";
import { EsriContextType } from "@/components/places/context/esri-context-type";
import CardSkeleton from "@/components/places/esri-map/card-skeleton";

import WrapperComponent from "@/components/places/esri-map/wrapper-component";
import useInfiniteScrollObs from "@/hooks/places/use-infinite-scroll";
import useVotes from "@/hooks/places/use-votes";
import useUser from "@/hooks/useUser";
import {
  resetMarker,
  setCurrentPageNumber,
  setPreviousMarker
} from "@/redux/features/place-marker/slice";
import {
  fetchPaginatedPins,
  page_size
} from "@/redux/features/place-markers/thunk";

import {
  setMapCenterCoordinates,
  setScrollPosition
} from "@/redux/features/place/slice";
import {
  setTriggerPoint,
  setUnverifiedModalOpen
} from "@/redux/features/unverified-modal/slice";
import useAccessStatus from "@/hooks/places/use-access-status";
import useShowUnverifiedModal from "@/hooks/places/use-unverfied-modal";

const SORT_ORDER = {
  newest: "created_at",
  topPins: "votes_count"
};

const EsriPinDetails = ({
  viewMarkerClickHandler,

  rightPanelProps
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);

  const { status } = useAppSelector((state) => state.placeMarkersSlice);
  const pinDetails = useAppSelector((state) => state.place.pinDetails);
  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const { accessStatus } = useAccessStatus();

  const count = useAppSelector((state) => state.placeMarkersSlice?.meta?.count);
  const totalMarkersCount = useAppSelector(
    (state) => state.placeMarkersSlice?.meta?.totalMarkersCount
  );
  const { mapRef, scrollRef } = useContext(EsriContext) as EsriContextType;
  const { isRegisteredUserLoggedIn, isUnverfiedUserLoggedIn } = useUser();

  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const pins = useAppSelector((state) => state.placeMarkersSlice.markers);

  const { language, translate } = useTranslator();
  const isAddressVisible = useAppSelector(
    (state) => state.place.place.showAddress
  );
  const { manageVotes, fetchPinDetailsWithSelectedData, findCurrentLikedPin } =
    useVotes();

  const { filteredCategoriesData } = useAppSelector(
    (state) => state.placeMarkersSlice
  );
  const { categories, isSelfPin, sortOrder } = filteredCategoriesData;
  const { id: userId } = useAppSelector(
    (state) => state.user.registeredUser.profile
  );
  const { currentPageNumber } = useAppSelector(
    (state) => state.placesMarkerSlice
  );

  const actionParams = {
    projectId,
    placesId,
    markerId: ""
  };

  const clickHandler = async (data) => {
    await dispatch(setScrollPosition(scrollRef?.current?.scrollTop));
    await dispatch(resetMarker());

    dispatch(
      setMapCenterCoordinates({
        lat: mapRef?.getCenter().lat,
        lng: mapRef?.getCenter().lng,
        zoom: mapRef?.getZoom()
      })
    );
    await mapRef?.setView([data?.lat, data?.lng], 20);
    await viewMarkerClickHandler(data, "activity-feed");
  };
  const currentPageRef = useRef(currentPageNumber);

  useEffect(() => {
    fetchPinDetailsWithSelectedData();
  }, [placesId]);

  useEffect(() => {
    isMobile && mapRef?.closePopup();
  }, [isMobile]);

  const handleInfiniteScroll = useCallback(() => {
    const pagination = {
      page_size,
      page: currentPageRef.current
    };

    const filters = {
      sort: { [SORT_ORDER[sortOrder]]: "desc" },
      category_id: categories,
      ...(isSelfPin && userId ? { user_id: userId } : {})
    };

    dispatch(
      fetchPaginatedPins({
        projectId,
        placesId,
        filters,
        pagination
      })
    );
    dispatch(setCurrentPageNumber(currentPageRef.current));
  }, [
    currentPageNumber,
    currentPageRef?.current,
    categories,
    userId,
    isSelfPin,
    sortOrder
  ]);

  const notFetching = !["fetching", "paginatingPins"].includes(status);
  const hasMorePins = pins.length < count;

  const lastPinRef = useInfiniteScrollObs<HTMLDivElement>(() => {
    currentPageRef.current += 1;
    handleInfiniteScroll();
  }, [notFetching, hasMorePins, currentPageNumber]);
  const { showUnverifiedModal } = useShowUnverifiedModal({ isNewPin: false });

  const handleVoteUp = (id) => {
    if (showUnverifiedModal()) {
      return;
    }

    if (accessStatus) {
      if (
        !isRegisteredUserLoggedIn &&
        !isUnverfiedUserLoggedIn &&
        isEmpty(Cookies.get("trigger_point"))
      ) {
        const filteredPin = findCurrentLikedPin(id, rightPanelView);

        const triggerPoint = {
          projectId,
          toolId: placesId,
          postId: id,
          triggeredSource: "vote",
          voteStatus: !filteredPin?.voted
        };

        Cookies.set("trigger_point", JSON.stringify(triggerPoint), {
          expires: 1,
          secure: true,
          sameSite: "Strict"
        });

        dispatch(setTriggerPoint(triggerPoint));
        dispatch(setUnverifiedModalOpen(true));

        return;
      }
    }
    manageVotes?.(id, rightPanelView);
  };

  const pinData: any = useCallback(() => {
    if (status === "fetching") {
      return <CardSkeleton />;
    }

    if (isEmpty(pins)) {
      return (
        <Box
          height="100vh"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Image
            alt={translate("common.poweredByEnagamentHQ")}
            src={addAssetPrefix("/images/map-illustration.png")}
          />
        </Box>
      );
    }

    return (
      <Box pt={4} background={"gray.100"}>
        {pins?.map((data, index, pins) => {
          const {
            id,
            address,
            comment,
            fileUrl,
            pinColor,
            pinIcon,
            pinName,
            userImageUrl,
            createdAt,
            author,
            markerUserId,
            voted,
            totalVotes
          }: any = data;

          const wrapperPadding = {
            pl: 4,
            pr: 4,
            pt: 10,
            pb: 4
          };

          const imageUrl = fileUrl?.includes("imgix.net")
            ? fileUrl
            : buildImgixUrl(fileUrl, 800, 800);

          const pinIconProps = {
            pinColor,
            pinIcon,
            pinId: id
          };

          return (
            <Box
              key={index}
              ref={pins.length - 1 === index ? lastPinRef : null}
              tabIndex={0}
              role="button"
              aria-label={`${translate("places.pin.cardFor", {
                pinName: pinName
              })}`}
              onKeyDown={(e) => {
                if (
                  (e.code === "Enter" || e.code === "Space") &&
                  e.target === e.currentTarget
                ) {
                  dispatch(setPreviousMarker(pins));
                  clickHandler(data);
                }
              }}
            >
              <Box {...wrapperPadding} key={id}>
                <Contribution
                  key={id}
                  cardVariant="unstyled"
                  username={author}
                  contributionTime={dateWithSiteLocale(
                    createdAt,
                    language.toLowerCase()
                  )}
                  handleOnBodyClick={() => {
                    dispatch(setPreviousMarker(pins));
                    clickHandler(data);
                  }}
                  isAddressVisible={isAddressVisible}
                  imageSrc={imageUrl}
                  imageAlt={pinName}
                  showImage={!!fileUrl}
                  cardDescription={comment ? stripAllHtml(comment) : ""}
                  showDescription
                  userAvatarSrc={userImageUrl}
                  showFooter={true}
                  pinType={pinName || "Pin"}
                  pinAddress={address}
                  tileBackgroundColor="gray.100"
                  isLoaded={!!pins.length}
                  pinColor={pinColor}
                  cardHeading=""
                  commentsCount={0}
                  showCommentIcon={false}
                  isThumbsUp={true}
                  thumbsUpCount={totalVotes || 0}
                  shareText=""
                  showDetailedText
                  hasBodyHighlight={false}
                  showMoreOptions={true}
                  isVoteActive={voted}
                  isIconButtonPressed={voted}
                  actionList={
                    <ActionList
                      actionParams={{
                        ...actionParams,
                        markerId: id,
                        userId: markerUserId
                      }}
                      rightPanelProps={rightPanelProps}
                    />
                  }
                  pinIcon={<PinIcon {...pinIconProps} />}
                  handleVoteUp={() => handleVoteUp(id)}
                  magnifiedImage={false}
                  isCursor={true}
                  ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
                    votesCount: totalVotes
                  })} ${
                    voted
                      ? translate("common.removeVote")
                      : translate("common.addVote")
                  }`}
                />
              </Box>
            </Box>
          );
        })}
        {status === "paginatingPins" && <CardSkeleton />}
      </Box>
    );
  }, [pins, status, rightPanelView, filteredCategoriesData, currentPageNumber]);

  const viewPinDetails: any = useCallback(() => {
    const markerProps = {
      rightPanelProps,
      currentPageRef,
      setCurrentPageNumber,
      viewMarkerClickHandler
    };
    return <MarkerDetails {...markerProps} />;
  }, [pinDetails, isMobile, rightPanelView, currentPageNumber, currentPageRef]);

  const scrollPositions = useCallback(() => {
    return !isMobile ? (
      <WrapperComponent
        bottom={{ base: "0px" }}
        position="absolute"
        right={{ base: "0px", lg: "1.25rem", sm: "0px" }}
        zIndex={999}
        top={{ base: "90vh" }}
      >
        <IconButton
          onClick={() =>
            document
              .getElementsByClassName("view-pin-details-scroller")?.[0]
              ?.scroll({ top: 0, behavior: "smooth" })
          }
          aria-label={translate("places.scrollToTop")}
          icon={<FiArrowUp />}
          size="lg"
          variant="solid"
          borderRadius="50%"
          color="gray.900"
          boxShadow="lg"
          bg={"white"}
          _hover={{ bg: "gray.300", color: "gray.900" }}
          _focus={{
            bg: "white",
            color: "gray.900"
          }}
        />
      </WrapperComponent>
    ) : null;
  }, [currentPageNumber, isMobile]);

  useEffect(() => {
    currentPageRef.current = currentPageNumber;
  }, [currentPageNumber]);

  const scrollPropsDesktop = {
    height: isMobile ? "90svh" : "75svh",
    ref: scrollRef,
    onScroll: scrollPositions
  };

  return (
    <>
      {["view-pin-details"].includes(rightPanelView) ? null : (
        <>
          <Box pl={"1.5rem"} pb={2}>
            <HStack
              as="section"
              justifyContent="space-between"
              color="gray.900"
            >
              {scrollPositions()}
              <Box display="flex" alignItems="center" gap={2} mt={6}>
                <Heading as="h2" fontWeight="bold" fontSize="xl">
                  {translate("places.feed")}
                </Heading>
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  bgColor="gray.50"
                  color="gray.800"
                  borderRadius={6}
                  gap={2}
                  px={2}
                >
                  {pins.length} / {totalMarkersCount ?? 0}
                </Text>
              </Box>
              <Box display="flex" justifyContent="right" p={4}>
                {!isMobile &&
                  !["create-pin", "view-pin-details"].includes(
                    rightPanelView
                  ) && <SortPins />}
              </Box>
            </HStack>
          </Box>
        </>
      )}
      <Box
        className="view-pin-details-scroller"
        overflowY={"auto"}
        {...scrollPropsDesktop}
      >
        {rightPanelView === "view-pin-details" ? viewPinDetails() : pinData()}
      </Box>
    </>
  );
};

export default memo(EsriPinDetails);
